import React, { useMemo, useState } from 'react';
import { Divider, Paper, Stack, Typography, Box } from '@esgian/esgianui';
import KpiChangeDisplay from '@components/Display/KPIChangeDisplay';
import { useSelector } from 'react-redux';
import { BASELINE_TYPE, DISPLAY_UNIT } from '@constants/index';
import {
  getBaselineDurationHours,
  getSelectedBaseline
} from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import { useBaselineQueries, useDisplayUnit } from '@hooks';
import { getPercentDiff, getTotSum } from '@helpers';
import { getMainDurationHours } from '@store/features/filters/MainPageSlice/MainPageSlice';
import { TextWithTooltipIcon } from '@components/Display';
import { findOverviewSignal } from '@api/helpers';

const getResultSumAvg = (signals, duration, roundNumber) => {
  let res = {};
  ['CO2', 'Energy', 'Fuel', 'NOx'].forEach(metricType => {
    const signal = findOverviewSignal(signals, 'Rig', metricType);
    const timeSeriesValues = signal?.timeSeriesValues || {};
    let sum = getTotSum(timeSeriesValues, true);
    res[`Rig${metricType}`] = sum / duration;
  });

  return res;
};

const getTargetSumAvg = (val, duration) => {
  if (val === 0) return 0;
  return val / duration;
};
// 759021.12644
function BaselineKeyFigures() {
  const [loading, setLoading] = useState(true);
  const { getUnitTitle, convertUnitValue } = useDisplayUnit();
  const mainDurationHours = useSelector(getMainDurationHours);
  const baselineDurationHours = useSelector(getBaselineDurationHours);
  const { baselineKeyFiguresQueries } = useBaselineQueries();
  const selectedBaseline = useSelector(getSelectedBaseline);

  const results = useMemo(() => {
    let mainData = baselineKeyFiguresQueries[0].data;
    let baselineData = baselineKeyFiguresQueries[1].data;
    if (!mainData) return {};
    
    if (selectedBaseline.baselineTypeId === BASELINE_TYPE.TIME_WELL) {
      mainData = getResultSumAvg(mainData.signals || [], mainDurationHours);
      if (!baselineData) return {};
      baselineData = getResultSumAvg(baselineData.signals || [], baselineDurationHours);
    } else {
      mainData = getResultSumAvg(mainData.signals || [], mainDurationHours, true);
      let duration = 24; // 24 hours per day
      if (!selectedBaseline.daily) {
        duration = mainDurationHours;
      }

      baselineData = {
        RigCO2: getTargetSumAvg(selectedBaseline.co2Emissions, duration),
        RigEnergy: getTargetSumAvg(selectedBaseline.energyConsumption, duration), // Stored in KWH on baseline
        RigFuel: getTargetSumAvg(selectedBaseline.fuelConsumption, duration),
        RigNOx: getTargetSumAvg(selectedBaseline.noxEmissions, duration)
      };
    }

    const mainHasData = Object.values(mainData).some((v) => v != null && v !== 0);
    if (!mainHasData) {
      setLoading(false);
      return {
        co2: null, co2Title: '', co2Diff: null,
        energy: null, energyDiff: null,
        fuel: null, fuelTitle: '', fuelDiff: null,
        nox: null, noxDiff: null
      };
    }

    let fuel = mainData.RigFuel - baselineData.RigFuel;
    let co2 = mainData.RigCO2 - baselineData.RigCO2;

    const { title: co2Title, converted: co2Conv } = getUnitTitle(Math.abs(co2), DISPLAY_UNIT.CO2);
    const { title: fuelTitle, converted: fuelConv } = getUnitTitle(
      Math.abs(fuel),
      DISPLAY_UNIT.FUEL
    );
    co2 = co2Conv ? convertUnitValue(co2, DISPLAY_UNIT.CO2, false) : co2;
    fuel = fuelConv ? convertUnitValue(fuel, DISPLAY_UNIT.FUEL, false) : fuel;
    setLoading(false);
    // Need average over time
    return {
      co2: co2,
      co2Title: co2Title,
      co2Diff: getPercentDiff(baselineData.RigCO2, mainData.RigCO2),
      energy: mainData.RigEnergy - baselineData.RigEnergy,
      energyDiff: getPercentDiff(baselineData.RigEnergy, mainData.RigEnergy),
      fuelTitle: fuelTitle,
      fuel: fuel,
      fuelDiff: getPercentDiff(baselineData.RigFuel, mainData.RigFuel),
      nox: mainData.RigNOx - baselineData.RigNOx,
      noxDiff: getPercentDiff(baselineData.RigNOx, mainData.RigNOx)
    };
  }, [baselineKeyFiguresQueries[0].data, baselineKeyFiguresQueries[1].data]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack justifyContent={'space-between'} direction={'row'}>
          <Box>
            <TextWithTooltipIcon
              tooltipEnterDelayMs={500}
              tooltipText={
                'The key figures indicates the difference between average per hour within the selected rig time period and the average per hour within the baseline time period.'
              }
              label={<Typography variant={'h6'}>Key figures</Typography>}     
            />
          </Box>
        </Stack>
        <Stack
          direction={'row'}
          justifyContent={'space-around'}
          divider={<Divider sx={{ height: 'inherit' }} orientation={'vertical'} />}>
          <KpiChangeDisplay
            loading={loading}
            value={results.energy}
            unit="kWh"
            percentChange={results.energyDiff}
            title={`Average energy consumption/hour`}
          />
          <KpiChangeDisplay
            loading={loading}
            value={results.fuel}
            unit={results.fuelTitle}
            percentChange={results.fuelDiff}
            title="Average fuel consumption/hour"
          />
          <KpiChangeDisplay
            loading={loading}
            value={results.co2}
            unit={results.co2Title}
            percentChange={results.co2Diff}
            title={
              <Typography variant={'caption'} color={'text.disabled'}>
                Average CO<sub>2</sub>e emissions/hour
              </Typography>
            }
          />
          <KpiChangeDisplay
            loading={loading}
            value={results.nox}
            percentChange={results.noxDiff}
            unit="kg"
            title={
              <Typography variant={'caption'} color={'text.disabled'}>
                Average NO<sub>x</sub>emissions/hour
              </Typography>
            }
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

BaselineKeyFigures.propTypes = {};

BaselineKeyFigures.defaultProps = {};

export default BaselineKeyFigures;
